import { HStack, IconButton, Text, Flex, Box, Menu, MenuButton, MenuList, MenuItem, Avatar, Divider, Button, Breadcrumb, BreadcrumbItem, BreadcrumbLink, useColorMode, VStack, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay, Badge } from '@chakra-ui/react'
import { Menu as MenuIcon, Mail, Settings, LogOut, User, Moon, Sun, Circle, Clock, Coffee, UserCheck } from 'lucide-react'
import { User as AppUser } from '@/types'
import { useLocation, Link } from 'react-router-dom'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useSiteSettings } from '@/hooks/useSiteSettings'
import { useUserStatusStore } from '@/store/userStatus'
import { useAuthStore } from '@/store/auth'
import { useMemo, useRef, useState, useEffect } from 'react'
import { isTeamLead } from '@/lib/utils/roleHelper'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { getInboxEmails } from '@/lib/services/inboxService'
import { useInboxNotificationsStore } from '@/store/inboxNotifications'

const INBOX_POLL_MS = 15_000
const NOTIF_SUBJECT_MAX = 100

function compactText(value: string | null | undefined): string {
  return (value || '').replace(/\s+/g, ' ').trim()
}

function truncateText(value: string, max: number): string {
  if (value.length <= max) return value
  return `${value.slice(0, max - 1)}…`
}

interface HeaderProps {
  onOpen: () => void
  user: AppUser | null
  onLogout: () => void
}

export function Header({ onOpen, user, onLogout }: HeaderProps) {
  const colors = useThemeColors()
  const location = useLocation()
  const pathname = location.pathname
  const segments = (pathname || '/').split('/').filter(Boolean)
  const toLabel = (s: string) => s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  const title = segments.length > 0 ? toLabel(segments[0]) : 'Dashboard'
  const crumbs = segments.map((seg, idx) => ({
    label: toLabel(seg),
    href: '/' + segments.slice(0, idx + 1).join('/'),
  }))
  const { colorMode, toggleColorMode } = useColorMode()
  const userStatus = useUserStatusStore((s) => s.status)
  const statusChangedAt = useUserStatusStore((s) => s.statusChangedAt)
  const setUserStatus = useUserStatusStore((s) => s.setStatus)
  const [isLogoutOpen, setIsLogoutOpen] = useState(false)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const authUser = useAuthStore((s) => s.user)
  const userIsTeamLead = isTeamLead(authUser)
  const { showError, showSuccess } = useToastNotification()
  const unreadCount = useInboxNotificationsStore((s) => s.unread_count)
  const unreadCountText = String(unreadCount ?? 0)
  const unreadDigits = unreadCountText.length
  const badgePx = unreadDigits <= 1 ? 0 : unreadDigits === 2 ? 1 : 2
  const badgeMinW = unreadDigits <= 1 ? '16px' : unreadDigits === 2 ? '20px' : '28px'
  const setInboxMeta = useInboxNotificationsStore((s) => s.setMeta)
  const inboxTotalRef = useRef(0)
  const inboxUnreadRef = useRef(0)
  const inboxTopIdRef = useRef<number | null>(null)
  const inboxFirstPollRef = useRef(true)

  /** Poll inbox meta app-wide so the bell badge stays current on any page */
  useEffect(() => {
    const tick = async () => {
      try {
        const res = await getInboxEmails({ per_page: 1, page: 1 })
        const latest = res.data[0]
        if (!inboxFirstPollRef.current) {
          const hasMetaIncrease =
            res.meta.total > inboxTotalRef.current ||
            res.meta.unread_count > inboxUnreadRef.current
          const hasNewTopId =
            Boolean(
              latest &&
                inboxTopIdRef.current !== null &&
                latest.id > inboxTopIdRef.current,
            )
          if (hasNewTopId || hasMetaIncrease) {
            const latestSubject = compactText(latest?.subject) || '(No subject)'
            const latestFrom = compactText(latest?.from_name || latest?.from_email)
            const detailParts = [
              `Subject: ${truncateText(latestSubject, NOTIF_SUBJECT_MAX)}`,
              latestFrom ? `From: ${latestFrom}` : null,
            ].filter(Boolean)
            showSuccess('New email received', detailParts.join(' · '))
          }
        } else {
          inboxFirstPollRef.current = false
        }
        inboxTotalRef.current = res.meta.total
        inboxUnreadRef.current = res.meta.unread_count
        inboxTopIdRef.current = latest ? latest.id : null
        setInboxMeta(res.meta.unread_count, res.meta.total, latest?.id ?? null)
      } catch {
        /* ignore poll errors */
      }
    }
    void tick()
    const id = window.setInterval(() => void tick(), INBOX_POLL_MS)
    return () => window.clearInterval(id)
  }, [setInboxMeta, showSuccess])

  // Auto-switch to online after break/lunch expires (not for offline)
  useEffect(() => {
    if (!statusChangedAt || userStatus === 'online' || userStatus === 'offline') return

    const checkInterval = setInterval(() => {
      const changedAt = new Date(statusChangedAt)
      const now = new Date()
      const elapsedMinutes = Math.floor((now.getTime() - changedAt.getTime()) / (1000 * 60))

      const durationMinutes = userStatus === 'lunch' ? 60 : 15 // lunch: 60min, break: 15min

      if (elapsedMinutes >= durationMinutes) {
        setUserStatus('online', (error) => {
          showError(error)
        })
      }
    }, 60000) // Check every minute

    return () => clearInterval(checkInterval)
  }, [statusChangedAt, userStatus, setUserStatus, showError])

  // Avatar background color based on user status
  const avatarBgColor = useMemo(() => {
    switch (userStatus) {
      case 'online':
        return '#22C55E' // Green for online
      case 'break':
        return '#F59E0B' // Amber/yellow for break
      case 'lunch':
        return '#EF4444' // Red/orange for lunch
      case 'offline':
        return '#6B7280' // Gray for offline
      default:
        return colors.primary
    }
  }, [userStatus, colors.primary])
  
  return (
    <Box bg={colors.cardBg} borderBottom="1px" borderColor={colors.border} px={6} py={4}>
      <Flex justify="space-between" align="center">
        <HStack align="start" spacing={3}>
          <IconButton
            aria-label="Open menu"
            icon={<MenuIcon />}
            variant="ghost"
            display="inline-flex"
            onClick={onOpen}
          />
          <Box display={{ base: 'none', md: 'block' }}>
            <Text fontSize="lg" fontWeight="semibold">{title}</Text>
            {segments.length > 0 && (
              <Breadcrumb fontSize="sm" color={colors.text} separator="/">
                <BreadcrumbItem key="home">
                  <BreadcrumbLink as={Link} to="/dashboard">Home</BreadcrumbLink>
                </BreadcrumbItem>
                {crumbs.map((c, i) => (
                  <BreadcrumbItem key={c.href} isCurrentPage={i === crumbs.length - 1}>
                    {i === crumbs.length - 1 ? (
                      <Text>{c.label}</Text>
                    ) : (
                      <BreadcrumbLink as={Link} to={c.href}>{c.label}</BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                ))}
              </Breadcrumb>
            )}
          </Box>
        </HStack>

        <HStack spacing={2}>
          <IconButton 
            aria-label="Toggle color mode" 
            icon={colorMode === 'light' ? <Moon size={18} /> : <Sun size={18} />} 
            variant="ghost"
            onClick={toggleColorMode}
          />
          <Box position="relative" display="inline-flex">
            <IconButton
              as={Link}
              to="/mailbox-inbox"
              aria-label="Unread emails — open mailbox inbox"
              icon={<Mail size={18} strokeWidth={2} aria-hidden />}
              variant="ghost"
            />
            {unreadCount > 0 && (
              <Badge
                position="absolute"
                top="0px"
                right="-15px"
                colorScheme="red"
                variant="solid"
                borderRadius="full"
                fontSize="0.65rem"
                minW={badgeMinW}
                h="16px"
                px={badgePx}
                display="flex"
                alignItems="center"
                justifyContent="center"
                pointerEvents="none"
              >
                {unreadCountText}
              </Badge>
            )}
          </Box>
          {userIsTeamLead && (
            <Menu>
              <MenuButton
                as={IconButton}
                aria-label="Team Overview"
                icon={<UserCheck size={18} />}
                variant="ghost"
              />
              <MenuList>
                <MenuItem icon={<UserCheck size={16} />} as={Link} to="/agent-status">
                  Agent Status
                </MenuItem>
              </MenuList>
            </Menu>
          )}
          <Menu>
            <MenuButton as={Button} variant="ghost" leftIcon={<Avatar size="sm" name={user?.user_login || ''} bg={avatarBgColor} color="white" />}>
              {user?.user_login ? user.user_login.split(' ')[0] : ''}
            </MenuButton>
            <MenuList>
              <Box px={2} py={2}>
                <Text fontSize="xs" fontWeight="600" textTransform="uppercase" letterSpacing="wider" color={colors.textSecondary} mb={2}>
                  User Status
                </Text>
                <VStack align="stretch" spacing={0}>
                  {[
                    { key: 'online' as const, label: 'Online', icon: Circle },
                    { key: 'break' as const, label: '1st/2nd Break', icon: Clock },
                    { key: 'lunch' as const, label: 'Lunch Break', icon: Coffee },
                  ].map(({ key, label, icon: Icon }) => (
                    <MenuItem
                      key={key}
                      onClick={() => {
                        setUserStatus(key, (error) => {
                          showError(error)
                        })
                      }}
                      bg={userStatus === key ? colors.sidebarActiveBg : 'transparent'}
                      color={userStatus === key ? colors.heading : colors.text}
                      icon={<Icon size={16} />}
                    >
                      {label}
                    </MenuItem>
                  ))}
                </VStack>
              </Box>
              <Divider />
              <MenuItem icon={<User />} as={Link} to="/profile">Profile</MenuItem>
              <Divider />
              <MenuItem icon={<LogOut />} onClick={() => setIsLogoutOpen(true)}>
                Logout
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>

      <AlertDialog
        isOpen={isLogoutOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsLogoutOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm Logout
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to logout? You will need to sign in again to access your account.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsLogoutOpen(false)}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={() => { setIsLogoutOpen(false); onLogout(); }} ml={3}>
                Logout
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  )
}

export default Header

